import { useState, useEffect, useMemo, useRef } from 'react';
import { flatten } from 'lodash';

export const useNavSearch = (search, resultLink, closeNav) => {
  const searchInput = useRef();

  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const flatSearchResults = useMemo(() => {
    return flatten(searchResults);
  }, [searchResults]);

  const setResultIndexes = results => {
    let universalIndex = 0;

    const resultsWithIndexes = results.map(array => {
      if (Array.isArray(array)) {
        return array.map(item => ({
          ...item,
          itemIndex: universalIndex++,
        }));
      } else {
        return {
          ...array,
          itemIndex: universalIndex++,
        };
      }
    });

    return resultsWithIndexes;
  };

  const navigateToResult = index => {
    window.location = resultLink(flatSearchResults[index]);
    closeNav && closeNav();
  };

  useEffect(() => {
    searchInput && searchInput.current && searchInput.current.focus();

    const observables = search(searchInput);

    const emptySub = observables.empty.subscribe(() => {
      setSearchResults([]);
      setHoverIndex(-1);
      setLoading(false);
    });

    const keysSub = observables.arrows.subscribe(keyCode => {
      if (keyCode === 38) {
        // UP ARROW key
        setHoverIndex(Math.max(0, hoverIndex - 1));
      } else if (keyCode === 40) {
        // DOWN ARROW key
        setHoverIndex(Math.min(hoverIndex + 1, flatSearchResults.length - 1));
      } else if (keyCode === 13) {
        // ENTER key
        if (flatSearchResults.length > 0 && hoverIndex >= 0) {
          navigateToResult(hoverIndex);
        } else if (flatSearchResults.length === 1) {
          navigateToResult(0);
        } else {
        }
      }
    });

    const resultsSub = observables.results.subscribe(results => {
      const resultsWithIndexes = setResultIndexes(results);
      setSearchResults(resultsWithIndexes);
      setHoverIndex(-1);
      setLoading(false);
    });

    return () => {
      resultsSub.unsubscribe();
      keysSub.unsubscribe();
      emptySub.unsubscribe();
    };
  }, [flatSearchResults, hoverIndex]);

  const handleSearch = e => {
    setLoading(true);
    setSearchString(e.target.value);
  };

  return [searchInput, loading, searchString, handleSearch, searchResults, hoverIndex];
};
